// The ED Referral Letter's templates and the first draft of its text
// (KMC request, 2026-10-28). Pure — tested by tests/referralLetter.test.mjs.
//
// The letter's fixed parts (letterhead, date, "To" block, salutation,
// "Thanking you,", signature) are printed from fields by the server
// (backend/utils/erReferralLetter.js). What this file writes is only the body:
// the paragraphs between the salutation and "Thanking you,". It is built from
// recorded facts and the doctor's own entries; a fact that is not recorded is
// left out, never filled with a guess. The doctor then edits the text freely.
//
// Template keys must match backend/utils/erReferralLetter.js#REFERRAL_TEMPLATES.

export type TemplateKey = 'specialist_evaluation' | 'higher_centre' | 'specialist_opinion';

export interface ReferralTemplate {
  key: TemplateKey;
  label: string;
  description: string;
}

export const REFERRAL_TEMPLATES: ReferralTemplate[] = [
  { key: 'specialist_evaluation', label: 'Specialist evaluation', description: '"Kindly evaluate … Kindly assess the patient for further management." The Kongu Medical Center format.' },
  { key: 'higher_centre', label: 'Transfer to a higher centre', description: '"We are referring … to your institution for further management. Kindly accept the patient …"' },
  { key: 'specialist_opinion', label: 'Opinion / consultation', description: '"Kindly see … and give your expert opinion. Kindly advise on further management …"' },
];

export interface ReferralFacts {
  arrived_at: string | null;
  chief_complaint: string | null;
  history: string | null;
  examination: string | null;
  assessment: string | null;
  vitals: string | null;
  vitals_at: string | null;
  treatment: string[];
  procedures: string[];
  investigations: string[];
  pending_investigations: string[];
  consults: string[];
  diagnosis: string | null;
  referral: {
    destination: string | null;
    reason: string | null;
    transfer_note: string | null;
    escort: string | null;
    ambulance: string | null;
  } | null;
}

export interface LetterPatient {
  display_name: string;
  gender: string | null;
  age: { years: number; approximate: boolean } | null;
}

/** The doctor's entries that feed the body. */
export interface BodyFields {
  template_key: TemplateKey;
  diagnosis: string;
  referral_reason: string;
  recommended_management: string;
}

const squash = (s: string | null | undefined) => (s ?? '').replace(/\s+/g, ' ').trim();

/** Ends a fragment with a full stop unless it already ends a sentence. */
export function sentence(s: string | null | undefined): string {
  const t = squash(s);
  if (!t) return '';
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

/** Drops a trailing full stop, for a phrase placed mid-sentence. */
function phrase(s: string | null | undefined): string {
  return squash(s).replace(/[.\s]+$/, '');
}

/** "45-year-old male", "approximately 30-year-old female", "male patient", or ''. */
export function describePatient(p: LetterPatient): string {
  const g = squash(p.gender).toLowerCase();
  const gender = g === 'male' || g === 'female' ? g : g ? g : '';
  if (p.age) {
    const a = `${p.age.approximate ? 'approximately ' : ''}${p.age.years}-year-old`;
    return gender ? `${a} ${gender}` : a;
  }
  return gender ? `${gender} patient` : '';
}

/** "28 Oct 2026 14:05" → "28 Oct 2026 at 14:05". */
function onDateAt(t: string | null): string {
  if (!t) return '';
  const m = t.match(/^(.*\d{4})\s+(\d{1,2}:\d{2})$/);
  return m ? `on ${m[1]} at ${m[2]}` : `on ${t}`;
}

function openingLine(key: TemplateKey, p: LetterPatient, facts: ReferralFacts, f: BodyFields): string {
  const who = [squash(p.display_name) || 'the patient', describePatient(p)].filter(Boolean).join(', ');
  const dx = phrase(f.diagnosis);
  const complaint = phrase(facts.chief_complaint);
  const about = dx ? `with ${dx}` : complaint ? `who presented with ${complaint}` : '';
  const subject = about ? `${who}, ${about}` : who;
  switch (key) {
    case 'higher_centre':
      return `We are referring ${subject}, to your institution for further management.`;
    case 'specialist_opinion':
      return `Kindly see ${subject}, and give your expert opinion.`;
    default:
      return `Kindly evaluate ${subject}.`;
  }
}

function closingLine(key: TemplateKey, f: BodyFields): string {
  const rec = phrase(f.recommended_management);
  const including = rec ? `, including ${rec}` : '';
  switch (key) {
    case 'higher_centre':
      return `Kindly accept the patient for further evaluation and management${including}.`;
    case 'specialist_opinion':
      return `Kindly advise on further management${including}.`;
    default:
      return `Kindly assess the patient for further management${including}.`;
  }
}

/** The clinical paragraph — only what the ED record holds. */
function clinicalParagraph(facts: ReferralFacts, f: BodyFields): string {
  const out: string[] = [];
  const complaint = phrase(facts.chief_complaint);
  if (facts.arrived_at || complaint) {
    out.push(`The patient presented to our Emergency Department${facts.arrived_at ? ` ${onDateAt(facts.arrived_at)}` : ''}${complaint ? ` with ${complaint}` : ''}.`);
  }
  if (facts.history) out.push(sentence(`History: ${facts.history}`));
  if (facts.vitals) out.push(sentence(`Vitals${facts.vitals_at ? ` (${facts.vitals_at})` : ''}: ${facts.vitals}`));
  if (facts.examination) out.push(sentence(`On examination: ${facts.examination}`));
  // The assessment is shown here only when it is not already the diagnosis.
  if (facts.assessment && phrase(facts.assessment) !== phrase(f.diagnosis)) out.push(sentence(`Assessment: ${facts.assessment}`));
  if (facts.investigations.length) out.push(sentence(`Investigations: ${facts.investigations.join('; ')}`));
  if (facts.pending_investigations.length) out.push(sentence(`Results awaited: ${facts.pending_investigations.join(', ')}`));
  if (facts.treatment.length) out.push(sentence(`Treatment given in the ED: ${facts.treatment.join('; ')}`));
  if (facts.procedures.length) out.push(sentence(`Procedures done: ${facts.procedures.join('; ')}`));
  if (facts.consults.length) out.push(sentence(`Specialist advice: ${facts.consults.join('; ')}`));
  if (facts.referral?.transfer_note) out.push(sentence(`Condition at referral: ${facts.referral.transfer_note}`));
  return out.join(' ');
}

function transferLine(facts: ReferralFacts): string {
  const r = facts.referral;
  if (!r || (!r.ambulance && !r.escort)) return '';
  const by = phrase(r.ambulance);
  const escort = phrase(r.escort);
  return `The patient is being transferred${by ? ` by ${by}` : ''}${escort ? `${by ? ',' : ''} escorted by ${escort}` : ''}.`;
}

/**
 * The letter body's first draft: opening, clinical summary, reason, closing —
 * paragraphs separated by a blank line (the server prints each as a <p>).
 */
export function composeReferralBody(p: LetterPatient, facts: ReferralFacts, f: BodyFields): string {
  const reason = sentence(phrase(f.referral_reason) ? `Reason for referral: ${phrase(f.referral_reason)}` : '');
  const paras = [
    openingLine(f.template_key, p, facts, f),
    clinicalParagraph(facts, f),
    [reason, transferLine(facts)].filter(Boolean).join(' '),
    closingLine(f.template_key, f),
  ];
  return paras.filter((x) => x.trim()).join('\n\n');
}

// ── "✨ Write with AI" ───────────────────────────────────────────────
// The AI writes only the body. The server adds the ED record and the
// hospital's active "Referral Letter" AI Assistant Role profile to the prompt
// (backend routes/ai.js with er_encounter_id); this message carries what the
// doctor typed in the form.

export interface AiLetterFields extends BodyFields {
  recipient: { doctor_name?: string; qualification?: string; designation?: string; hospital?: string; city?: string };
}

/** The message sent to the assistant for the letter body. */
export function buildAiLetterRequest(f: AiLetterFields): string {
  const tpl = REFERRAL_TEMPLATES.find((t) => t.key === f.template_key);
  const r = f.recipient;
  const to = [r.doctor_name && drName(r.doctor_name), r.qualification, r.designation, r.hospital, r.city].map(squash).filter(Boolean).join(', ');
  const lines = [
    'Write the Referral Letter for this patient from the ED record.',
    'Write ONLY the body paragraphs that go between the salutation and "Thanking you," — no letterhead, date, "To" address, salutation, closing or signature; the hospital system prints those.',
    'Separate paragraphs with one blank line. Plain sentences: no headings, bullet points, tables, bold text or fill-in fields.',
    `Letter type: ${tpl?.label ?? 'Specialist evaluation'}.`,
    to ? `Addressed to: ${to}.` : '',
    squash(f.diagnosis) ? `Diagnosis (as written by the doctor): ${squash(f.diagnosis)}.` : 'No diagnosis given — describe the presenting problem instead; do not invent a diagnosis.',
    squash(f.referral_reason) ? `Reason for referral: ${squash(f.referral_reason)}.` : '',
    squash(f.recommended_management) ? `Recommended evaluation / treatment: ${squash(f.recommended_management)}.` : '',
  ];
  return lines.filter(Boolean).join('\n');
}

const SALUTATION_RE = /^(respected|dear|to whomsoever)\b/i;
const CLOSING_RE = /^(thanking you|thank you|yours (sincerely|faithfully|truly)|with (kind |warm )?regards|regards)\b/i;
const HEADER_RE = /^(to\b|date\s*:|ref(erence)?\s*:|sub(ject)?\s*:|re\s*:)/i;

/**
 * The AI's document text → just the letter body: drops anything before a
 * salutation it added anyway (address block, date), everything from a closing
 * ("Thanking you," …) on, and markdown it was asked not to use.
 */
export function letterBodyFromAiReply(docText: string): string {
  let lines = (docText ?? '').replace(/\r\n?/g, '\n').split('\n');
  const sal = lines.slice(0, 12).findIndex((l) => SALUTATION_RE.test(l.trim()));
  if (sal !== -1) lines = lines.slice(sal + 1);
  else {
    // No salutation: drop a leading "To / Date / Subject" header block.
    while (lines.length && (HEADER_RE.test(lines[0]!.trim()) || !lines[0]!.trim())) lines.shift();
  }
  const close = lines.findIndex((l) => CLOSING_RE.test(l.replace(/[*_]/g, '').trim()));
  if (close !== -1) lines = lines.slice(0, close);
  return lines
    .map((l) => l.replace(/\*\*(.+?)\*\*/g, '$1').replace(/(^|\s)\*(\S.*?)\*(?=\s|$|[.,;:])/g, '$1$2').replace(/^#+\s*/, '').trimEnd())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** "Sekar" / "Dr.Sekar" → "Dr. Sekar" — the server prints names the same way. */
export function drName(name: string | null | undefined): string {
  const n = squash(name).replace(/^(dr\.?\s*|doctor\s+)/i, '');
  return n ? `Dr. ${n}` : '';
}

/** 'YYYY-MM-DD' → 'DD/MM/YYYY'. */
export function fmtLetterDate(iso: string | null | undefined): string {
  const m = String(iso ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso ?? '');
}

/** What still blocks finalizing — mirrors backend finalizeProblems (the server decides). */
export function missingForFinal(l: {
  recipient: { doctor_name?: string; hospital?: string };
  referral_reason: string;
  body: string;
  signature: { doctor_name?: string };
}): string[] {
  const out: string[] = [];
  if (!squash(l.recipient.doctor_name) && !squash(l.recipient.hospital)) out.push('the receiving doctor or hospital');
  if (!squash(l.referral_reason)) out.push('the reason for referral');
  if (!squash(l.body)) out.push('the letter text');
  if (!squash(l.signature.doctor_name)) out.push("the referring doctor's name");
  return out;
}
